import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AudienceSelector, { type AudienceValue } from './AudienceSelector';
import MarkdownBodyField from './MarkdownBodyField';
import ScheduleDialog from './ScheduleDialog';
import { describeApiError } from '../errorMessage';
import {
  previewAudienceSize,
  type ReleaseNote,
  type ReleaseNotePayload,
} from '../../services/releaseNotes.service';

/** Which button the admin pressed — each implies a different publishAt/status. */
export type SaveIntent = 'draft' | 'schedule' | 'publish';

interface Props {
  open: boolean;
  /** The note being edited, or null when creating a new one. */
  note: ReleaseNote | null;
  onClose: () => void;
  onSave: (payload: ReleaseNotePayload, intent: SaveIntent) => Promise<void>;
}

const EMPTY_AUDIENCE: AudienceValue = { mode: 'all', orgIds: [], userIds: [] };

/** `datetime-local` needs `YYYY-MM-DDTHH:mm` in local time, not an ISO string. */
const toLocalInputValue = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const ReleaseNoteEditor: React.FC<Props> = ({ open, note, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AudienceValue>(EMPTY_AUDIENCE);
  const [sendEmail, setSendEmail] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [publishAt, setPublishAt] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPublished = note?.status === 'published';

  // Reset the form each time the dialog opens so a previous edit can't leak in.
  useEffect(() => {
    if (!open) return;
    setTitle(note?.title ?? '');
    setVersion(note?.version ?? '');
    setSummary(note?.summary ?? '');
    setBody(note?.body ?? '');
    setAudience(
      note
        ? {
            mode: note.audience?.mode ?? 'all',
            orgIds: note.audience?.orgIds ?? [],
            userIds: note.audience?.userIds ?? [],
          }
        : EMPTY_AUDIENCE,
    );
    // Default ON for a new note: emailing the audience is the point of the
    // feature, and an unchecked box silently produces a note that reaches nobody
    // by mail. Editing an existing note keeps whatever it was saved with.
    setSendEmail(note ? note.sendEmail : true);
    setShowModal(note?.showModal ?? true);
    setPublishAt(toLocalInputValue(note?.publishAt ?? null));
    setScheduleOpen(false);
    setError(null);
  }, [open, note]);

  // Ask the server how many people the current selection actually resolves to,
  // so the admin sees the blast radius before publishing.
  useEffect(() => {
    if (!open) return;
    const incomplete =
      (audience.mode === 'orgs' && audience.orgIds.length === 0) ||
      (audience.mode === 'users' && audience.userIds.length === 0);
    if (incomplete) {
      setAudienceCount(0);
      return;
    }

    let cancelled = false;
    setAudienceCount(null);
    (async () => {
      try {
        const res = await previewAudienceSize({
          mode: audience.mode,
          orgIds: audience.orgIds,
          userIds: audience.userIds,
        });
        if (!cancelled) setAudienceCount(res?.data?.count ?? 0);
      } catch {
        if (!cancelled) setAudienceCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, audience]);

  const validationError = useMemo(() => {
    if (!title.trim()) return 'Title is required.';
    if (!body.trim()) return 'Body is required.';
    if (audience.mode === 'orgs' && audience.orgIds.length === 0)
      return 'Select at least one organisation.';
    if (audience.mode === 'users' && audience.userIds.length === 0)
      return 'Select at least one user.';
    if (!sendEmail && !showModal)
      return 'Pick at least one delivery method — email or in-app.';
    return null;
  }, [title, body, audience, sendEmail, showModal]);

  const buildPayload = useCallback(
    (intent: SaveIntent, scheduledFor?: string): ReleaseNotePayload => ({
      title: title.trim(),
      version: version.trim() || undefined,
      summary: summary.trim() || undefined,
      body,
      audience: {
        mode: audience.mode,
        orgIds: audience.mode === 'orgs' ? audience.orgIds : undefined,
        userIds: audience.mode === 'users' ? audience.userIds : undefined,
      },
      sendEmail,
      showModal,
      // A published note keeps its original date; rescheduling it is meaningless.
      // Otherwise the intent decides: only "schedule" carries a date, and saving a
      // draft explicitly clears one so a previously-scheduled note stops being due.
      publishAt: isPublished
        ? undefined
        : intent === 'schedule' && scheduledFor
          ? new Date(scheduledFor).toISOString()
          : null,
    }),
    [title, version, summary, body, audience, sendEmail, showModal, isPublished],
  );

  /** Returns true on success, so callers know whether to close their own sub-dialog. */
  const handleSave = async (
    intent: SaveIntent,
    scheduledFor?: string,
  ): Promise<boolean> => {
    if (validationError) {
      setError(validationError);
      return false;
    }
    setIsSaving(true);
    setError(null);
    try {
      await onSave(buildPayload(intent, scheduledFor), intent);
      onClose();
      return true;
    } catch (err: any) {
      // Stay open so the admin can correct and retry.
      setError(describeApiError(err, 'Failed to save release note'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    {/* Hidden, not unmounted, while the schedule dialog is up — unmounting would
        reset every field the admin just filled in. */}
    <Dialog
      open={open && !scheduleOpen}
      onClose={isSaving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {note ? 'Edit release note' : 'New release note'}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isPublished && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This note is already published. Edits update the in-app copy — they do not
            re-send the email. Use Resend for that.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            fullWidth
            required
            disabled={isSaving}
          />
          <TextField
            label="Version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            size="small"
            placeholder="v2.4.0"
            disabled={isSaving}
            sx={{ minWidth: { sm: 160 } }}
          />
        </Stack>

        <TextField
          label="Summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          size="small"
          fullWidth
          disabled={isSaving}
          helperText="One line, used as the email preview text."
          sx={{ mb: 2 }}
        />

        <MarkdownBodyField value={body} onChange={setBody} disabled={isSaving} />

        <Divider sx={{ my: 2 }} />

        <AudienceSelector
          value={audience}
          onChange={setAudience}
          audienceCount={audienceCount}
          disabled={isSaving}
        />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={isSaving}
              />
            }
            label="Send email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showModal}
                onChange={(e) => setShowModal(e.target.checked)}
                disabled={isSaving}
              />
            }
            label="Show in-app on next login"
          />
        </Box>

        {note?.status === 'scheduled' && note.publishAt && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Currently scheduled for {new Date(note.publishAt).toLocaleString()}.
            Saving as a draft clears that schedule.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSaving} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave('draft')}
          disabled={isSaving}
          variant="outlined"
        >
          Save draft
        </Button>
        {!isPublished && (
          <Button
            onClick={() => setScheduleOpen(true)}
            disabled={isSaving}
            variant="outlined"
            startIcon={<ScheduleOutlinedIcon />}
          >
            Schedule
          </Button>
        )}
        {!isPublished && (
          <Button
            onClick={() => void handleSave('publish')}
            disabled={isSaving}
            variant="contained"
          >
            Publish now
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/*
      Sibling of the editor Dialog, not a child. MUI renders each Dialog into its
      own portal, and nesting one inside another's subtree puts the inner dialog
      under the outer's focus trap — it opens but can't be interacted with.
    */}
    <ScheduleDialog
      open={scheduleOpen}
      initialValue={publishAt}
      isSaving={isSaving}
      sendEmail={sendEmail}
      onClose={() => setScheduleOpen(false)}
      onConfirm={async (when) => {
        const ok = await handleSave('schedule', when);
        if (ok) setScheduleOpen(false);
      }}
    />
    </>
  );
};

export default ReleaseNoteEditor;
